import React from "react";
import PopupDialog from "components/dialog/PopupDialog";
import DataList from "components/datatable/DataList";
import { useAuth } from "context/AuthProvider";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { boolText, parseBoolean, showEmpty } from "features/kms/system/SystemPageUtils";

const emptyPasswordForm = {
  id_user: "",
  nama: "",
  username: "",
  password: "",
  password_confirmation: "",
  must_change_password: true,
};

function PasswordDialog({ show, item, onClose, onSaved }) {
  const [showMsg] = useMsgBox();
  const [, , loading, axiosFetch] = useAxiosFunction();
  const [form, setForm] = React.useState(emptyPasswordForm);

  React.useEffect(() => {
    if (!show) return;
    setForm({
      ...emptyPasswordForm,
      id_user: item?.id_user || "",
      nama: item?.nama || "",
      username: item?.username || "",
    });
  }, [show, item]);

  const canSave =
    String(form.password || "").trim().length > 0 &&
    String(form.password || "") === String(form.password_confirmation || "") &&
    !loading;

  const save = async () => {
    if (!canSave) return;
    const resp = await axiosFetch({
      method: "PUT",
      url: `/administrasi/pengguna/${form.id_user}/password`,
      auth: true,
      data: {
        password: form.password,
        must_change_password: Boolean(form.must_change_password),
      },
    });
    if (resp?.data?.success === true) {
      await showMsg("Informasi", "Password berhasil diubah.");
      onSaved?.();
      return;
    }
    await showMsg("Error", resp?.message || "Password gagal diubah.");
  };

  return (
    <PopupDialog
      title="Ubah Password"
      size="md"
      show={show}
      closeHandle={onClose}
      closeLabel="Batal"
      doHandle={save}
      doLabel={loading ? "Menyimpan..." : "Simpan"}
      doDisabled={!canSave}
    >
      <div className="form-group">
        <label>Nama</label>
        <input className="form-control" value={form.nama || ""} disabled />
      </div>
      <div className="form-group">
        <label>Username</label>
        <input className="form-control" value={form.username || ""} disabled />
      </div>
      <div className="form-group">
        <label>Password Baru</label>
        <input
          className="form-control"
          type="password"
          value={form.password}
          onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
        />
      </div>
      <div className="form-group">
        <label>Ulangi Password Baru</label>
        <input
          className="form-control"
          type="password"
          value={form.password_confirmation}
          onChange={(event) =>
            setForm((current) => ({ ...current, password_confirmation: event.target.value }))
          }
        />
        {form.password_confirmation && form.password !== form.password_confirmation ? (
          <div className="invalid-feedback d-block">Konfirmasi password belum sama.</div>
        ) : null}
      </div>
      <div className="form-check">
        <input
          id="must_change_password"
          className="form-check-input"
          type="checkbox"
          checked={Boolean(form.must_change_password)}
          onChange={(event) =>
            setForm((current) => ({ ...current, must_change_password: event.target.checked }))
          }
        />
        <label className="form-check-label" htmlFor="must_change_password">
          Pengguna wajib mengganti password saat login berikutnya
        </label>
      </div>
    </PopupDialog>
  );
}

const emptyPersonalForm = {
  nik: "",
  nama: "",
  password: "",
};

function PersonalDialog({ show, onClose, onSaved }) {
  const [showMsg] = useMsgBox();
  const [, , loading, axiosFetch] = useAxiosFunction();
  const [, , checkingNIK, checkNIKFetch] = useAxiosFunction();
  const [form, setForm] = React.useState(emptyPersonalForm);
  const [nikStatus, setNikStatus] = React.useState({
    checked: false,
    available: false,
    message: "",
  });

  React.useEffect(() => {
    if (show) {
      setForm(emptyPersonalForm);
      setNikStatus({ checked: false, available: false, message: "" });
    }
  }, [show]);

  const nik = String(form.nik || "").replace(/\D/g, "").slice(0, 16);
  const nama = String(form.nama || "").trim();
  const password = String(form.password || "").trim();

  React.useEffect(() => {
    if (!show) return undefined;
    if (nik.length !== 16) {
      setNikStatus({ checked: false, available: false, message: "" });
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setNikStatus({ checked: false, available: false, message: "Memeriksa NIK..." });
      const resp = await checkNIKFetch({
        method: "GET",
        url: `/administrasi/pengguna/personal/check-nik?nik=${encodeURIComponent(nik)}`,
        auth: true,
      });
      if (cancelled) return;
      setNikStatus({
        checked: true,
        available: Boolean(resp?.data?.available),
        message: resp?.data?.message || resp?.message || "",
      });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [show, nik, checkNIKFetch]);

  const saveHint = (() => {
    if (nik.length !== 16) {
      return "NIK wajib diisi 16 digit.";
    }
    if (!nikStatus.checked || checkingNIK) {
      return "NIK sedang diperiksa.";
    }
    if (!nikStatus.available) {
      return nikStatus.message || "NIK sudah terdaftar.";
    }
    if (nama === "") {
      return "Nama wajib diisi.";
    }
    if (password.length < 8) {
      return "Password minimal 8 karakter.";
    }
    return "";
  })();
  const canSave =
    nik.length === 16 &&
    nikStatus.checked &&
    nikStatus.available &&
    nama !== "" &&
    password.length >= 8 &&
    !checkingNIK &&
    !loading;

  const updateForm = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const save = async () => {
    if (!canSave) return;
    const resp = await axiosFetch({
      method: "POST",
      url: "/administrasi/pengguna/personal",
      auth: true,
      data: {
        nik,
        nama: form.nama,
        password: form.password,
      },
    });
    if (resp?.data?.success === true) {
      const username = resp?.data?.username || nik;
      await showMsg(
        "Informasi",
        `Personal berhasil ditambahkan. Username login adalah ${username}. Login pertama wajib menggunakan password awal yang diisi pada form ini, lalu pengguna harus mengganti password dan melengkapi profil.`,
      );
      onSaved?.();
      return;
    }
    await showMsg("Error", resp?.message || "Personal gagal disimpan.");
  };

  return (
    <PopupDialog
      title="Tambah Personal"
      size="md"
      show={show}
      closeHandle={onClose}
      closeLabel="Batal"
      doHandle={save}
      doLabel={loading ? "Menyimpan..." : "Simpan"}
      doDisabled={!canSave}
    >
      <div className="form-group">
        <label>NIK</label>
        <input
          className="form-control"
          value={nik}
          maxLength={16}
          onChange={(event) => updateForm("nik", event.target.value.replace(/\D/g, "").slice(0, 16))}
          placeholder="16 digit NIK"
        />
        {nik.length === 16 && nikStatus.message ? (
          <div className={nikStatus.available ? "valid-feedback d-block" : "invalid-feedback d-block"}>
            {nikStatus.message}
          </div>
        ) : null}
      </div>
      <div className="form-group">
        <label>Nama</label>
        <input
          className="form-control"
          value={form.nama}
          onChange={(event) => updateForm("nama", event.target.value)}
          placeholder="Nama lengkap"
        />
      </div>
      <div className="form-group mb-0">
        <label>Password</label>
        <input
          className="form-control"
          type="password"
          value={form.password}
          onChange={(event) => updateForm("password", event.target.value)}
          placeholder="Minimal 8 karakter"
        />
        <small className="form-text text-muted">
          Pengguna wajib mengganti password ini saat login pertama.
        </small>
        {saveHint ? <div className="invalid-feedback d-block mt-2">{saveHint}</div> : null}
      </div>
    </PopupDialog>
  );
}

export default function PengelolaanPenggunaPage() {
  const { user } = useAuth();
  const [showMsg, confirmMsg] = useMsgBox();
  const [, , toggleLoading, axiosFetch] = useAxiosFunction();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [personalDialogOpen, setPersonalDialogOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [optionsResp] = useAxios({ method: "GET", url: "/administrasi-pengguna-options", auth: true });

  const groupOptions = Array.isArray(optionsResp?.data?.groups) ? optionsResp.data.groups : [];
  const contextFilter = optionsResp?.data?.context_filter || {};
  const personalCreateOptions = optionsResp?.data?.personal_create || {};
  const canCreatePersonal =
    Boolean(personalCreateOptions?.allowed) &&
    ["ADMIN_KONI_KABKOTA", "ADMIN_CABOR", "ADMIN_KLUB"].includes(
      String(user?.groupname || "").toUpperCase(),
    );
  const contextOptions = Array.isArray(contextFilter?.options) ? contextFilter.options : [];
  const filters = [
    { dtype: "q", name: "q", colsize: 3 },
    {
      dtype: "dropdown",
      name: "groupname",
      label: "Peran",
      colsize: 3,
      options: groupOptions,
      valueId: "id",
      labelId: "label",
      placeholder: "-Semua Peran-",
    },
  ];
  if (contextFilter?.name && contextOptions.length > 0) {
    filters.push({
      dtype: "dropdown",
      name: contextFilter.name,
      label: contextFilter.label || "Organisasi",
      colsize: 3,
      options: contextOptions,
      valueId: "id",
      labelId: "label",
      placeholder: contextFilter.placeholder || "-Semua Organisasi-",
    });
  }

  const openPasswordDialog = (item) => {
    setSelected(item);
    setDialogOpen(true);
  };

  const closePasswordDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const saved = () => {
    closePasswordDialog();
    setReloadKey((current) => current + 1);
  };

  const toggleUserActive = async (item) => {
    if (toggleLoading) return;
    const isActive = parseBoolean(item?.is_active);
    const actionLabel = isActive ? "menonaktifkan" : "mengaktifkan";
    const ok = await confirmMsg(
      "Konfirmasi",
      `Apakah Anda yakin akan ${actionLabel} akun ${item?.username || "ini"}?`,
    );
    if (!ok) return;

    const resp = await axiosFetch({
      method: "GET",
      url: `/administrasi/pengguna-toggle-active/${item?.id_user}`,
      auth: true,
    });
    if (resp?.data?.success === true) {
      await showMsg("Informasi", `Akun berhasil ${isActive ? "dinonaktifkan" : "diaktifkan"}.`);
      setReloadKey((current) => current + 1);
      return;
    }
    await showMsg("Error", resp?.message || "Status akun gagal diubah.");
  };

  const personalSaved = () => {
    setPersonalDialogOpen(false);
    setReloadKey((current) => current + 1);
  };

  const coltype = {
    id_user: { type: "hidden", name: "id_user" },
    nama: { label: "Nama", renderFunc: (val) => showEmpty(val), width: 260 },
    username: { label: "Username", renderFunc: (val) => showEmpty(val), width: 180 },
    email: { label: "Email", renderFunc: (val) => showEmpty(val), width: 220 },
    group_descriptions: { label: "Group", renderFunc: (val) => showEmpty(val), renderExportFunc: (val) => showEmpty(val), width: 260 },
    is_active: {
      label: "Status",
      renderFunc: (val) => boolText(val),
      renderExportFunc: (val) => boolText(val),
      align: "center",
      width: 90,
    },
    action: { label: "Aksi", align: "center", width: 120 },
  };

  return (
    <>
      <DataList
        title="Pengelolaan Pengguna"
        coltype={coltype}
        reload={reloadKey}
        toolbar={
          canCreatePersonal
            ? [
                {
                  label: "Tambah Personal",
                  icon: "fa-user-plus",
                  onClick: () => setPersonalDialogOpen(true),
                },
              ]
            : []
        }
        dlist={{
          url: "/administrasi/pengguna",
          columns: ["no", "nama", "username", "email", "group_descriptions", "is_active", "action"],
          filter: filters,
          sort_by: ["nama", "username", "email", "group_descriptions", "is_active"],
          render_action_extension: (val, col, item) => (
            <>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  openPasswordDialog(item);
                }}
                className="btn btn-primary btn-xs"
                title="Ubah Password"
              >
                <i className="fa fa-lock"></i>
              </a>
              &nbsp;
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  toggleUserActive(item);
                }}
                className={parseBoolean(item?.is_active) ? "btn btn-warning btn-xs" : "btn btn-success btn-xs"}
                title={parseBoolean(item?.is_active) ? "Nonaktifkan" : "Aktifkan"}
              >
                <i className={parseBoolean(item?.is_active) ? "fa fa-ban" : "fa fa-check"}></i>
              </a>
              &nbsp;
            </>
          ),
        }}
        pdflist={{
          title: "Pengelolaan Pengguna",
          columns: ["no", "nama", "username", "email", "group_descriptions", "is_active"],
          orientation: "landscape",
        }}
        xlslist={{
          title: "Pengelolaan Pengguna",
          columns: ["no", "nama", "username", "email", "group_descriptions", "is_active"],
        }}
      />
      <PasswordDialog
        show={dialogOpen}
        item={selected}
        onClose={closePasswordDialog}
        onSaved={saved}
      />
      <PersonalDialog
        show={personalDialogOpen}
        options={personalCreateOptions}
        onClose={() => setPersonalDialogOpen(false)}
        onSaved={personalSaved}
      />
    </>
  );
}
